import api from "../../config/api";
import { handleApiError } from "@/utils/ErrorHandler";

const state = {
    workgroups: [],
    activities: [],
    skills: [],
    workgroupDetails: null,
    loading: false,
    error: null,
    saving: false,
};

const mutations = {
    SET_WORKGROUPS(state, data) {
        state.workgroups = data;
    },
    SET_ACTIVITIES(state, data) {
        state.activities = data;
    },
    SET_SKILLS(state, data) {
        state.skills = data;
    },
    SET_WORKGROUP_DETAILS(state, data) {
        state.workgroupDetails = data;
    },
    SET_LOADING(state, value) {
        state.loading = value;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_SAVING(state, value) {
        state.saving = value;
    },
};

const actions = {

    /**
     * Fetch workgroup details by workgroup ID
     * @param {Number|String} workgroupId - Workgroup ID
     */
    async fetchWorkgroupById({ commit }, workgroupId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.get(`/activity-workgroups/${workgroupId}`);
            // API returns { output: { ... } }
            const data = response.data?.output || response.data?.data || response.data || null;

            commit("SET_WORKGROUP_DETAILS", data);
            return data;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch workgroup details");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * Save or update a workgroup (via manageworkgroupajax API)
     * @param {Object} payload - Workgroup data
     */
    async saveWorkgroup({ commit }, payload) {
        commit("SET_SAVING", true);
        commit("SET_ERROR", null);

        var request = {
            cmd: "editworkgroup",
            ...payload
        }
        try {
            const response = await api.post("/manageworkgroupajax", request);
            const data = response.data?.message;
            return data;
        } catch (error) {
            const message = handleApiError(error, "Failed to save workgroup");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_SAVING", false);
        }
    },

    /**
     * Fetch activities for a given location
     * @param {Number|String} locationId - Location ID
     * @param {Boolean} all - Whether to fetch all activities (default: true)
     */
    async fetchActivitiesByLocation({ commit }, { locationId, all = true }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.get(`/activities/location/${locationId}`, {
                params: { all },
            });
            const data = response.data?.data || [];

            commit("SET_ACTIVITIES", data);
            return data;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch activities by location");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * Fetch skills for a given location
     * @param {Number|String} locationId - Location ID
     * @param {Boolean} all - Whether to fetch all skills (default: true)
     */
    async fetchSkillsByLocation({ commit }, { locationId, all = true }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.get(`/skills/location/${locationId}`, {
                params: { all },
            });
            const data = response.data?.data?.output || [];

            commit("SET_SKILLS", data);
            return data;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch skills by location");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    workgroups: (state) => state.workgroups,
    activitiesByLocation: (state) => state.activities,
    skillsByLocation: (state) => state.skills,
    workgroupDetails: (state) => state.workgroupDetails,
    workgroupsLoading: (state) => state.loading,
    workgroupsSaving: (state) => state.saving,
    workgroupsError: (state) => state.error,
};

export default {
    state,
    mutations,
    actions,
    getters,
};